// ABOUTME: Visual indicator for offline status and sync state
// ABOUTME: Shows banner when offline and sync progress when syncing

import {
  ArrowPathIcon,
  CheckCircleIcon,
  CloudIcon,
  ExclamationTriangleIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline'
import { AnimatePresence, motion } from 'framer-motion'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'

/**
 * Offline indicator component
 * Displays a banner when the user is offline or when sync is in progress
 */
export default function OfflineIndicator() {
  const { online, syncing, pendingOperations, failedOperations } = useNetworkStatus()

  // Don't show anything if online and no issues
  if (online && !syncing && pendingOperations === 0 && failedOperations === 0) {
    return null
  }

  return (
    <AnimatePresence>
      {!online && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-50 bg-warning-500 dark:bg-warning-600 text-white px-4 py-2 shadow-lg"
        >
          <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <SignalSlashIcon className="w-4 h-4" />
            <span>You're offline. Changes will sync when connection is restored.</span>
          </div>
        </motion.div>
      )}

      {online && syncing && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-50 bg-primary-500 dark:bg-primary-600 text-white px-4 py-2 shadow-lg"
        >
          <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <ArrowPathIcon className="w-4 h-4 animate-spin" />
            <span>Syncing your data...</span>
          </div>
        </motion.div>
      )}

      {online && !syncing && pendingOperations > 0 && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-50 bg-surface-elevated dark:bg-surface-elevated text-white px-4 py-2 shadow-lg"
        >
          <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <CloudIcon className="w-4 h-4" />
            <span>{pendingOperations} changes waiting to sync</span>
          </div>
        </motion.div>
      )}

      {online && !syncing && failedOperations > 0 && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-50 bg-error-500 dark:bg-error-600 text-white px-4 py-2 shadow-lg"
        >
          <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <ExclamationTriangleIcon className="w-4 h-4" />
            <span>{failedOperations} sync operations failed. Will retry automatically.</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * Compact offline badge for use in headers/navigation
 */
export function OfflineBadge() {
  const { online, syncing, pendingOperations } = useNetworkStatus()

  if (online && !syncing && pendingOperations === 0) {
    return null
  }

  return (
    <div className="flex items-center gap-1">
      {!online && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
          <SignalSlashIcon className="w-3 h-3" />
          Offline
        </span>
      )}

      {online && syncing && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
          <ArrowPathIcon className="w-3 h-3 animate-spin" />
          Syncing
        </span>
      )}

      {online && !syncing && pendingOperations > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface text-fg-primary dark:bg-surface dark:text-fg-tertiary">
          <CloudIcon className="w-3 h-3" />
          {pendingOperations}
        </span>
      )}
    </div>
  )
}

/**
 * Network status icon for minimal displays
 */
export function NetworkStatusIcon() {
  const { online, syncing } = useNetworkStatus()

  if (!online) {
    return <SignalSlashIcon className="w-4 h-4 text-warning-500" />
  }

  if (syncing) {
    return <ArrowPathIcon className="w-4 h-4 text-primary-500 animate-spin" />
  }

  return <CheckCircleIcon className="w-4 h-4 text-success-500" />
}
